import { createSlice, PayloadAction, SliceCaseReducers } from "@reduxjs/toolkit";
import {
  ActionType,
  CauseType,
  ExtraType,
  JobCategoryType,
  JobSymptomType,
  ManufacturerType,
  PartType,
  PlannerNodesType,
  ServiceLevelType,
  SortGroupsType,
  SupplierType,
} from "operations/schema/schema";
import { asyncQueries, queryBuilder } from "./cache.queries";

export type CustomerPartsCache = {
  [x: string]: PartType[];
};
export type AvailablePartsCache = {
  engineerParts: PartType[];
  customerParts: CustomerPartsCache;
  locationalParts: PartType[];
  liveSearchParts: PartType[];
};
export type Loading = {
  prefill: boolean;
  actionsAndCauses: boolean;
  availableParts: boolean;
  customerParts: boolean;
  engineerParts: boolean;
  extras: boolean;
  jobCategories: boolean;
  locationalParts: boolean;
  manufacturers: boolean;
  partSortGroups: boolean;
  plannerNodes: boolean;
  requestableParts: boolean;
  serviceLevels: boolean;
  suggestedParts: boolean;
  suppliers: boolean;
  symptoms: boolean;
};
export type State = {
  lastLoaded: string | null;
  availableParts: AvailablePartsCache;
  actions: ActionType[];
  causes: CauseType[];
  extras: ExtraType[];
  jobCategories: JobCategoryType[];
  manufacturers: ManufacturerType[];
  partSortGroups: SortGroupsType;
  plannerNodes: PlannerNodesType[];
  requestableParts: PartType[];
  serviceLevels: ServiceLevelType[];
  suggestedParts: {
    [x: string]: string[];
  };
  suppliers: SupplierType[];
  symptoms: JobSymptomType[];
  loading: Loading;
  engineerPartsLastLoaded: string | null;
  requestablePartsLastLoaded: string | null;
};

interface Actions extends SliceCaseReducers<State> {
  setData: <K extends Extract<keyof State, string>>(
    state: State,
    action: PayloadAction<{
      key: K;
      data: State[K];
    }>
  ) => State;
  setLoading: <K extends Extract<keyof Loading, string>>(
    state: State,
    action: PayloadAction<{
      key: K;
      data: Loading[K];
    }>
  ) => State;
}

export const initialState: State = {
  lastLoaded: null,
  availableParts: {
    engineerParts: [],
    customerParts: {},
    locationalParts: [],
    liveSearchParts: [],
  },
  actions: [],
  causes: [],
  extras: [],
  jobCategories: [],
  manufacturers: [],
  partSortGroups: {},
  plannerNodes: [],
  requestableParts: [],
  serviceLevels: [],
  suggestedParts: {},
  suppliers: [],
  symptoms: [],
  engineerPartsLastLoaded: null,
  requestablePartsLastLoaded: null,
  loading: {
    prefill: false,
    actionsAndCauses: false,
    availableParts: false,
    customerParts: false,
    engineerParts: false,
    extras: false,
    jobCategories: false,
    locationalParts: false,
    manufacturers: false,
    partSortGroups: false,
    plannerNodes: false,
    requestableParts: false,
    serviceLevels: false,
    suggestedParts: false,
    suppliers: false,
    symptoms: false,
  },
};
const actions: Actions = {
  setData: (state, { payload: { key, data } }) => {
    state[key] = data;
    return state;
  },
  setLoading: (state, { payload: { key, data } }) => {
    state.loading[key] = data;
    return state;
  },
};

const cacheStore = createSlice<State, Actions>({
  name: "cache",
  initialState,
  reducers: actions,
  extraReducers: queryBuilder,
});

export default cacheStore.reducer;
export const { setData, setLoading } = cacheStore.actions;
export const {
  getCachePrefill,
  //  getActionsAndCauses,
  //  getJobCategories,
  //  getManufacturers,
  getExtras,
  getAvailableParts,
  getPartsCustomer,
  getPartsEngineer,
  getPartsLocational,
  //  getPartSortGroups,
  getPlannerNodes,
  getRequestableParts,
  //  getServiceLevels,
  getSuggestedParts,
  //  getSuppliers,
  getSymptoms,
} = asyncQueries;
